import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Box, Zoom } from "@mui/material";
import { styled as materialStyled } from "@mui/material/styles";
import { North, South } from "@mui/icons-material";
import { atom, useAtom } from "jotai";
import {
  type AppetizeNetworkLog,
  type AppetizeCloneNetworkLog,
} from "../../utils/networkLogSchemas";
import { CustomFab } from "./components";
import {
  isAppetizeNetworkLog,
  NetworkLogDetailTabs,
  NetworkLogsTable,
} from "@/components/test-editor-gpt/network-logs/NetworkLogs";
import { useGridApiRef } from "@mui/x-data-grid-pro";

export type AnyNetworkLog = AppetizeNetworkLog | AppetizeCloneNetworkLog;

interface NetworkTabProps {
  networkLogs: AnyNetworkLog[];
  playbackTimestamp: number;
  display: boolean;
  /** Run start time in seconds since epoch — used to compute relativeTimestamp
   *  for Appetize Clone network logs (which only carry timestamp_start). */
  startedTimeInEpoch?: number;
}

const NetworkTab = ({
  networkLogs,
  playbackTimestamp,
  display,
  startedTimeInEpoch,
}: NetworkTabProps) => {
  const apiRef = useGridApiRef();

  const [forceMoveToTimestampNetworkTab, setForceMoveToTimestampNetworkTab] =
    useAtom(forceMoveToTimestampNetworkTabAtom);

  // Appetize Clone logs carry timestamp_start (epoch seconds) but no
  // relativeTimestamp. Derive it so the playback timeline works the same way
  // as for Appetize logs.
  const logsWithRelativeTimestamp = useMemo<AnyNetworkLog[]>(() => {
    if (!startedTimeInEpoch) return networkLogs;
    return networkLogs.map((log) => {
      if (isAppetizeNetworkLog(log)) return log;
      if (log.timestamp_start == null) return log;
      return {
        ...log,
        relativeTimestamp: (log.timestamp_start - startedTimeInEpoch) * 1000,
      };
    });
  }, [networkLogs, startedTimeInEpoch]);

  const filteredLogs = useMemo(
    () =>
      logsWithRelativeTimestamp.filter((networkLog) => {
        if (isAppetizeNetworkLog(networkLog)) {
          return (
            networkLog.type === "response" &&
            networkLog.request.method &&
            networkLog.request.url &&
            networkLog.response?.status != null
          );
        }
        // AppetizeCloneNetworkLog: include any log that has a method
        return networkLog.method != null;
      }),
    [logsWithRelativeTimestamp]
  );

  const currentTimestampIndex = useMemo(() => {
    return filteredLogs.findLastIndex((log) => {
      const rt = (log as any).relativeTimestamp ?? null;
      return rt != null && rt <= playbackTimestamp;
    });
  }, [filteredLogs, playbackTimestamp]);

  useEffect(() => {
    if (forceMoveToTimestampNetworkTab == null) return;

    const timestamp = forceMoveToTimestampNetworkTab;
    const indexToJump = filteredLogs.findLastIndex((log) => {
      const rt = (log as any).relativeTimestamp ?? null;
      return rt != null && timestamp >= rt;
    });

    setForceMoveToTimestampNetworkTab(null);

    if (indexToJump < 0) return;

    // DataGrid is always mounted (display:none when hidden) so apiRef is valid.
    apiRef.current?.scrollToIndexes({ rowIndex: indexToJump });
  }, [
    forceMoveToTimestampNetworkTab,
    filteredLogs,
    setForceMoveToTimestampNetworkTab,
  ]);

  const [jumpToTimestamp, setJumpToTimestamp] = useState<
    "hidden" | "top" | "bottom"
  >("hidden");

  const handleScrollChange = useCallback(() => {
    if (!apiRef.current || filteredLogs.length === 0) {
      setJumpToTimestamp("hidden");
      return;
    }

    const renderContext = apiRef.current.state?.virtualization?.renderContext;
    if (!renderContext) {
      setJumpToTimestamp("hidden");
      return;
    }

    const firstVisibleIndex = renderContext.firstRowIndex ?? 0;
    const lastVisibleIndex = renderContext.lastRowIndex ?? 0;

    if (currentTimestampIndex < 0) {
      setJumpToTimestamp("hidden");
    } else if (currentTimestampIndex < firstVisibleIndex) {
      setJumpToTimestamp("top");
    } else if (currentTimestampIndex > lastVisibleIndex) {
      setJumpToTimestamp("bottom");
    } else {
      setJumpToTimestamp("hidden");
    }
  }, [filteredLogs.length, currentTimestampIndex]);

  useEffect(() => {
    handleScrollChange();
  }, [playbackTimestamp, handleScrollChange]);

  // Subscribe to DataGrid scroll events so the FAB appears when the user manually scrolls
  useEffect(() => {
    if (!display) return;
    return apiRef.current?.subscribeEvent("scrollPositionChange", handleScrollChange);
  }, [display, handleScrollChange]);

  const jumpToTimestampHandler = useCallback(() => {
    if (currentTimestampIndex >= 0 && apiRef.current) {
      apiRef.current.scrollToIndexes({ rowIndex: currentTimestampIndex });
      setJumpToTimestamp("hidden");
    }
  }, [currentTimestampIndex]);

  // Keep the DataGrid always mounted (CSS-hidden when not displayed) so that
  // apiRef stays valid and scrollToIndexes works when jumping from another tab.
  return (
    <NetworkLogsTableWrapper sx={{ display: display ? "flex" : "none" }}>
      <Zoom in={jumpToTimestamp !== "hidden"} unmountOnExit>
        <CustomFab
          variant="extended"
          jumpToTimestamp={jumpToTimestamp}
          onClick={jumpToTimestampHandler}
        >
          {jumpToTimestamp !== "top" ? <South /> : <North />}
          Jump to current timestamp
        </CustomFab>
      </Zoom>
      <NetworkLogsTable
        logs={filteredLogs}
        playbackTimestamp={playbackTimestamp}
        currentTimestampIndex={currentTimestampIndex}
        apiRef={apiRef}
        containerSx={{ flex: 1, minHeight: 0, width: "100%" }}
        enableQuickFilterHotkey={false}
      />
    </NetworkLogsTableWrapper>
  );
};

const forceMoveToTimestampNetworkTabAtom = atom<null | number>(null);

const NetworkLogsTableWrapper = materialStyled(Box)({
  display: "flex",
  flexDirection: "column",
  gridArea: "tab-content",
  position: "relative",
  backgroundColor: "#232323",
  boxShadow:
    "0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)",
  borderRadius: "4px",
  padding: 0,
  margin: "16px",
});

export { forceMoveToTimestampNetworkTabAtom, NetworkLogDetailTabs };

export default NetworkTab;
